import React, { useState } from 'react';
import { withTranslation } from 'react-i18next';
import * as PropTypes from 'prop-types';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useAuth } from '../../AuthContext';

/** Dialog to confirm that an assessment should be deleted (and delete it) */
function ConfirmDeleteDialog({ selectedAssessment, open, onClose, onDeleted, t }) {
  const { jwtToken } = useAuth();
  const [deleting, setDeleting] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState('');
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  const confirmDelete = () => {
    setDeleteErrorMessage('');
    setDeleting(true);
    fetch(`${process.env.REACT_APP_CCVI_API_URL}/assessment/${selectedAssessment.author}/${selectedAssessment.assessmentUuid}`,
      {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${jwtToken}`, 'Content-Type': 'application/json' },
      })
      .then(async res => {
        const data = await res.json();
        setDeleting(false);
        if (res.ok) {
          if (onDeleted) {
            onDeleted();
          }
          setDeleteSuccess(true);
        } else {
          throw new Error(data.msg || `${res.status} ${res.statusText}`);
        }
        return data;
      })
      .catch((e) => {
        setDeleteErrorMessage(e);
        setDeleting(false);
      });
  };

  const headerKey = deleteSuccess ? 'successHeader' : 'confirmDeleteHeader';

  const content = deleteSuccess ? (
    <Alert severity='success'>
      {t('confirmDeleteDialog.successMessage')}
    </Alert>
  ) : (
    <DialogContentText id='delete-dialog-description'>
      {t('confirmDeleteDialog.confirmDeleteMessage', { species: selectedAssessment.scientificName })}
    </DialogContentText>
  );

  let actions;
  if (deleting) {
    actions = <LinearProgress />;
  } else if (deleteSuccess) {
    actions = (
      <DialogActions>
        <Button onClick={onClose} variant='contained' color='primary'>
          {t('confirmDeleteDialog.close')}
        </Button>
      </DialogActions>
    );
  } else {
    actions = (
      <DialogActions>
        <Button onClick={onClose}>{t('confirmDeleteDialog.cancel')}</Button>
        <Button onClick={confirmDelete} variant='contained' color='error'>
          {t('confirmDeleteDialog.delete')}
        </Button>
      </DialogActions>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby='delete-dialog-title'
      aria-describedby='delete-dialog-description'
      TransitionProps={{
        onExited: () => {
          setDeleteErrorMessage('');
          setDeleteSuccess(false);
          setDeleting(false);
        }
      }}
    >
      <DialogTitle id='delete-dialog-title'>{t(`confirmDeleteDialog.${headerKey}`)}</DialogTitle>
      <DialogContent>
        {content}
        {!!deleteErrorMessage && (
          <Alert severity='error'>
            {t('confirmDeleteDialog.couldNotDelete', { error: deleteErrorMessage })}
          </Alert>
        )}
      </DialogContent>
      {actions}
    </Dialog>
  );
}

ConfirmDeleteDialog.propTypes = {
  /** The id of the assessment to delete */
  selectedAssessment: PropTypes.shape({
    assessmentUuid: PropTypes.string,
    author: PropTypes.string,
    scientificName: PropTypes.string
  }),
  /** Whether the dialog is visible */
  open: PropTypes.bool,
  /** Callback that will be called when the dialog should be closed */
  onClose: PropTypes.func,
  /** Callback that will be called when the assessment is succesfully deleted */
  onDeleted: PropTypes.func,
  /** react-i18next function for translation */
  t: PropTypes.func.isRequired,
};

export default withTranslation('translations')(ConfirmDeleteDialog);
